import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, limit, query, Timestamp, where } from 'firebase/firestore';
import { db } from '@/firebase';
import './OrderShippingPrint.css';

interface DocumentTemplate {
    id: string;
    title: string;
    body: string;
    body2?: string;
    body3?: string;
    body4?: string;
    body5?: string;
    bodies?: string[];
    sortNo?: number;
    templateType?: string;
    paymentMethod?: string;
    course?: string;
}

const normalizeValue = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    if (value instanceof Timestamp) {
        return value.toDate().toLocaleDateString('ja-JP');
    }
    if (value instanceof Date) {
        return value.toLocaleDateString('ja-JP');
    }
    if (typeof value === 'object') {
        return JSON.stringify(value, null, 2);
    }
    return String(value);
};

const resolvePathValue = (source: Record<string, any>, path: string): unknown => {
    const tokens = Array.from(path.matchAll(/([^[.\]]+)|\[(\d+)\]/g)).map((match) =>
        match[1] ?? Number(match[2])
    );
    return tokens.reduce<unknown>((current, token) => {
        if (current == null) return undefined;
        return (current as Record<string, any>)[token as keyof typeof current];
    }, source);
};

const resolveTemplateText = (
    template: string,
    data: {
        order: Record<string, any>;
        user: Record<string, any>;
        shikaku: Record<string, any>;
        paymentSummary: Record<string, any>;
    }
) =>
    template.replace(/\{\{\s*(order|user|shikaku|paymentSummary)\.([^\s}]+)\s*\}\}/g, (_match, group, path) => {
        const source = data[group as keyof typeof data] ?? {};
        return normalizeValue(resolvePathValue(source as Record<string, any>, path));
    });

export default function OrderShippingPrint() {
    const { id } = useParams<{ id: string }>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [orderData, setOrderData] = useState<Record<string, any> | null>(null);
    const [userData, setUserData] = useState<Record<string, any>>({});
    const [shikakuData, setShikakuData] = useState<Record<string, any>>({});
    const [template, setTemplate] = useState<DocumentTemplate | null>(null);

    useEffect(() => {
        document.body.classList.add('order-shipping-print');
        return () => {
            document.body.classList.remove('order-shipping-print');
        };
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            if (!id) {
                setError('Order IDが指定されていません');
                setLoading(false);
                return;
            }

            try {
                const orderDoc = await getDoc(doc(db, 'Order', id));
                if (!orderDoc.exists()) {
                    setError('Orderが見つかりませんでした');
                    return;
                }

                const order = { orderId: orderDoc.id, ...orderDoc.data() };
                setOrderData(order);

                const userRef = order.UserRef || order.userId;
                if (userRef) {
                    const userDocRef = userRef.path ? userRef : doc(db, 'User', userRef);
                    const userSnapshot = await getDoc(userDocRef);
                    if (userSnapshot.exists()) {
                        setUserData(userSnapshot.data() as Record<string, any>);
                    }
                }

                let shikakuRef = order.shikakuId;
                if (shikakuRef && !shikakuRef.path) {
                    shikakuRef = doc(db, 'Shikaku', shikakuRef);
                }
                if (shikakuRef) {
                    const shikakuSnapshot = await getDoc(shikakuRef);
                    if (shikakuSnapshot.exists()) {
                        setShikakuData(shikakuSnapshot.data() as Record<string, any>);
                    }
                }

                const paymentMethod = typeof order.paymentMethod === 'string' ? order.paymentMethod.trim() : '';
                const course = typeof order.course === 'string' ? order.course.trim() : '';
                const normalizedPaymentMethod = paymentMethod || 'default';
                const normalizedCourse = course || 'default';
                const templateCandidates = [
                    { paymentMethod: normalizedPaymentMethod, course: normalizedCourse },
                    { paymentMethod: normalizedPaymentMethod, course: 'default' },
                    { paymentMethod: 'default', course: normalizedCourse },
                    { paymentMethod: 'default', course: 'default' },
                ];
                const uniqueCandidates = Array.from(
                    new Map(
                        templateCandidates.map((candidate) => [
                            `${candidate.paymentMethod}__${candidate.course}`,
                            candidate,
                        ])
                    ).values()
                );

                for (const candidate of uniqueCandidates) {
                    const templateQuery = query(
                        collection(db, 'dc_templates'),
                        where('templateType', '==', 'shipping_paper'),
                        where('paymentMethod', '==', candidate.paymentMethod),
                        where('course', '==', candidate.course),
                        limit(1)
                    );
                    const templateSnapshot = await getDocs(templateQuery);
                    if (!templateSnapshot.empty) {
                        const templateDoc = templateSnapshot.docs[0];
                        setTemplate({ id: templateDoc.id, ...(templateDoc.data() as Omit<DocumentTemplate, 'id'>) });
                        return;
                    }
                }

                const anyTemplateQuery = query(
                    collection(db, 'dc_templates'),
                    where('templateType', '==', 'shipping_paper'),
                    limit(1)
                );
                const anyTemplateSnapshot = await getDocs(anyTemplateQuery);
                if (!anyTemplateSnapshot.empty) {
                    const templateDoc = anyTemplateSnapshot.docs[0];
                    setTemplate({ id: templateDoc.id, ...(templateDoc.data() as Omit<DocumentTemplate, 'id'>) });
                    return;
                }

                setTemplate(null);
            } catch (err: any) {
                console.error('Error fetching shipping template:', err);
                setError(err?.message || '発送用紙テンプレートの取得に失敗しました');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id]);

    const orderDetailId = orderData?.orderId || id;
    const orderNumberLabel = orderData?.orderNumber || orderData?.orderId || '-';

    const renderedBody = useMemo(() => {
        if (!template) return [];
        const bodyPages =
            Array.isArray(template.bodies) && template.bodies.length
                ? template.bodies
                : [template.body, template.body2, template.body3, template.body4, template.body5].filter(
                      (value, index, array) => {
                          if (value) return true;
                          return array.slice(index + 1).some((later) => later);
                      }
                  );
        const payload = {
            order: orderData ?? {},
            user: userData,
            shikaku: shikakuData,
            paymentSummary: (orderData?.paymentSummary ?? {}) as Record<string, any>,
        };
        return bodyPages.length
            ? bodyPages.map((page) => resolveTemplateText(page || '', payload))
            : [resolveTemplateText(template.body || '', payload)];
    }, [template, orderData, userData, shikakuData]);

    const buildPreviewHtml = () => `<!doctype html>
<html lang="ja">
  <head>
    <meta charset="utf-8" />
    <title>発送用紙</title>
  </head>
  <body style="margin:0; padding:0;">
    ${renderedBody.join('')}
  </body>
</html>`;

    const handlePrint = () => {
        if (!renderedBody.length) return;

        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        iframe.srcdoc = buildPreviewHtml();

        const cleanup = () => {
            iframe.remove();
            window.removeEventListener('afterprint', cleanup);
        };

        iframe.onload = () => {
            try {
                iframe.contentWindow?.focus();
                iframe.contentWindow?.print();
            } finally {
                window.addEventListener('afterprint', cleanup);
                setTimeout(cleanup, 1000);
            }
        };

        document.body.appendChild(iframe);
    };

    if (loading) {
        return <div className="p-6">Loading...</div>;
    }

    if (error) {
        return <div className="p-6 text-red-500">{error}</div>;
    }

    return (
        <div className="order-shipping-print__page">
            <div className="order-shipping-print__header">
                <div>
                    <div className="order-shipping-print__title">発送用紙</div>
                    <div className="order-shipping-print__meta">
                        注文番号:{' '}
                        {orderDetailId ? (
                            <Link
                                to={`/admin/orders/${orderDetailId}`}
                                className="text-emerald-600 hover:underline"
                            >
                                {orderNumberLabel}
                            </Link>
                        ) : (
                            orderNumberLabel
                        )}{' '}
                        / 支払方法: {orderData?.paymentMethod || '-'} / コース: {orderData?.course || '-'}
                    </div>
                </div>
                <div className="order-shipping-print__controls">
                    <Link
                        to={`/admin/orders/${orderDetailId}`}
                        className="rounded border border-gray-300 px-3 py-1 text-sm text-gray-600 hover:bg-gray-100"
                    >
                        戻る
                    </Link>
                    <button
                        type="button"
                        onClick={handlePrint}
                        className="rounded bg-emerald-500 px-3 py-1 text-sm font-semibold text-white hover:bg-emerald-600"
                    >
                        印刷
                    </button>
                </div>
            </div>
            {template ? (
                <div className="order-shipping-print__content">
                    <div className="order-shipping-print__preview-frame">
                        <iframe
                            title="発送用紙プレビュー"
                            className="order-shipping-print__preview-iframe"
                            srcDoc={buildPreviewHtml()}
                        />
                    </div>
                </div>
            ) : (
                <div className="text-sm text-gray-500">
                    該当する発送用紙テンプレートが見つかりませんでした。テンプレート管理で作成してください。
                </div>
            )}
        </div>
    );
}
